import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Link
} from '@mui/material';
import { ExpandMore, Visibility } from '@mui/icons-material';
import { formatDate, formatShortFriendlyDateWithTime } from '../../../helpers';
import { IVendorContract } from '../../../models';

interface IMobileInProgressContracts {
  original: IVendorContract;
}

export const MobileInProgressContracts: FC<IMobileInProgressContracts> = ({ original }) => {
  const classes = useStyles();
  const {
    vendorName,
    pandaDocContractType,
    pandaDocContractName,
    pandaDocDocumentStatuses,
    dateStarted,
    contractAge,
    lastChange,
    linkToDocument
  } = original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <div>
            <Typography variant="body2">{pandaDocContractName}</Typography>
            <Typography className={classes.activeName} variant="body2">
              {vendorName}
            </Typography>
          </div>
          <div className={classes.buttonsWrapper}>
            {linkToDocument && (
              <IconButton
                LinkComponent={Link}
                href={linkToDocument}
                target="_blank"
                className={classes.editButton}
                color="primary"
                size="large"
              >
                <Visibility />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Contract Type:
          </FormLabel>
          <Typography component="span" variant="body2">
            {pandaDocContractType || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Status:
          </FormLabel>
          <Typography component="span" variant="body2">
            {pandaDocDocumentStatuses || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Date Started:
          </FormLabel>
          <Typography component="span" variant="body2">
            {dateStarted ? formatDate(dateStarted) : '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Contract Age:
          </FormLabel>
          <Typography component="span" variant="body2">
            {contractAge || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Last Changed:
          </FormLabel>
          <Typography component="span" variant="body2">
            {lastChange ? formatShortFriendlyDateWithTime(lastChange) : '--'}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  editButton: {
    padding: `2px 5px`
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  activeName: {
    color: theme.palette.grey[500]
  },
  divider: {
    margin: '0 5px'
  }
}));
